import React, { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useTranscript, useTranscripts } from "@/hooks/transcripts/useTranscripts";
import { useTranscriptsUI } from "./TranscriptsProvider";
import { useFilteredTranscripts } from "./useFilteredTranscripts";
import { TranscriptList } from "./TranscriptList";
import { TranscriptThread } from "./TranscriptThread";
import { TranscriptContextPanel } from "./TranscriptContextPanel";
import { EmptyTranscriptState } from "./EmptyTranscriptState";
import { TranscriptUploadDialog } from "./TranscriptUploadDialog";

/**
 * The full-page transcripts experience: a three-pane resizable layout on
 * desktop (list · transcript · context), a list ⇄ detail stack on mobile.
 */
export const TranscriptsPage: React.FC = () => {
	const isMobile = useIsMobile();
	const { selectedId, setSelectedId, contextPanelOpen, setContextPanelOpen } = useTranscriptsUI();
	const listQuery = useTranscripts();
	const filtered = useFilteredTranscripts(listQuery.data);
	const [uploadOpen, setUploadOpen] = useState(false);

	// The context panel reads from the same detail cache the thread fills.
	const { data: selectedTranscript } = useTranscript(selectedId);

	// Keyboard navigation: ↑/↓ or j/k across the filtered list, Escape closes.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (
				target &&
				(target.tagName === "INPUT" ||
					target.tagName === "TEXTAREA" ||
					target.tagName === "SELECT" ||
					target.isContentEditable)
			) {
				return;
			}
			const isNext = event.key === "ArrowDown" || event.key === "j";
			const isPrevious = event.key === "ArrowUp" || event.key === "k";
			if (isNext || isPrevious) {
				if (filtered.length === 0) return;
				event.preventDefault();
				const currentIndex = filtered.findIndex((transcript) => transcript.id === selectedId);
				const nextIndex =
					currentIndex === -1
						? 0
						: Math.min(Math.max(currentIndex + (isNext ? 1 : -1), 0), filtered.length - 1);
				setSelectedId(filtered[nextIndex].id);
			} else if (event.key === "Escape" && selectedId) {
				event.preventDefault();
				setSelectedId(null);
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [filtered, selectedId, setSelectedId]);

	const showContext = contextPanelOpen && !!selectedTranscript;

	const list = (
		<TranscriptList
			transcripts={listQuery.data}
			isLoading={listQuery.isLoading}
			isError={listQuery.isError}
			isFetching={listQuery.isFetching}
			refetch={listQuery.refetch}
			onUpload={() => setUploadOpen(true)}
		/>
	);

	const uploadDialog = (
		<TranscriptUploadDialog
			open={uploadOpen}
			onOpenChange={setUploadOpen}
			onUploaded={(id) => setSelectedId(id)}
		/>
	);

	if (isMobile) {
		return (
			<div className="relative h-full min-h-0 overflow-hidden bg-background">
				{selectedId ? (
					<div className="h-full animate-slide-in">
						<TranscriptThread
							transcriptId={selectedId}
							onBack={() => setSelectedId(null)}
							contextPanelOpen={contextPanelOpen}
							onToggleContextPanel={() => setContextPanelOpen(!contextPanelOpen)}
							onDeleted={() => setSelectedId(null)}
						/>
						<Sheet open={contextPanelOpen && !!selectedTranscript} onOpenChange={setContextPanelOpen}>
							<SheetContent side="right" className="w-[85vw] max-w-sm p-0">
								<SheetTitle className="sr-only">{selectedTranscript?.subject || ""}</SheetTitle>
								{selectedTranscript && <TranscriptContextPanel transcript={selectedTranscript} />}
							</SheetContent>
						</Sheet>
					</div>
				) : (
					list
				)}
				{uploadDialog}
			</div>
		);
	}

	return (
		<>
			<ResizablePanelGroup
				direction="horizontal"
				autoSaveId="transcripts-panels"
				className="h-full min-h-0 bg-background"
			>
				<ResizablePanel defaultSize={28} minSize={20} maxSize={40} className="min-w-[280px]">
					{list}
				</ResizablePanel>
				<ResizableHandle />
				<ResizablePanel defaultSize={showContext ? 50 : 72} minSize={35}>
					{selectedId ? (
						<TranscriptThread
							transcriptId={selectedId}
							contextPanelOpen={contextPanelOpen}
							onToggleContextPanel={() => setContextPanelOpen(!contextPanelOpen)}
							onDeleted={() => setSelectedId(null)}
						/>
					) : (
						<EmptyTranscriptState />
					)}
				</ResizablePanel>
				{showContext && (
					<>
						<ResizableHandle />
						<ResizablePanel defaultSize={22} minSize={16} maxSize={32} className="min-w-[240px]">
							<TranscriptContextPanel transcript={selectedTranscript!} />
						</ResizablePanel>
					</>
				)}
			</ResizablePanelGroup>
			{uploadDialog}
		</>
	);
};
